import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Star, ArrowLeft, Clock, DollarSign } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getServicesByCategory, Service } from '../supabase/queries';

const ServiceCard: React.FC<{ service: Service }> = ({ service }) => (
  <div className="bg-gray-800 p-4 rounded-xl shadow-lg flex items-center justify-between mb-4">
    <div className="flex items-center">
      {/* Service icon or initial could go here if needed */}
      <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
        {service.name.charAt(0).toUpperCase()}
      </div>
      <div>
        <h3 className="text-white font-semibold text-lg">{service.name}</h3>
        <p className="text-gray-400 text-sm">{service.description}</p>
        <div className="flex items-center text-gray-500 text-xs mt-1">
          <Clock className="w-3 h-3 mr-1" />
          <span>{service.duration_value} {service.duration_type}</span>
          <DollarSign className="w-3 h-3 ml-3 mr-1" />
          <span>{service.price.toFixed(2)}</span>
        </div>
      </div>
    </div>
    <div className="flex flex-col items-end">
      <button className="bg-teal-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-teal-700 transition">
        Book
      </button>
    </div>
  </div>
);

const ServiceListPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1); // Go back to the previous page
  };
  const [services, setServices] = useState<Service[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!categoryId) {
      setError('Category ID is missing.');
      setLoading(false);
      return;
    }

    const fetchServices = async () => {
      setLoading(true);
      setError(null);
      const fetchedServices = await getServicesByCategory(categoryId);
      if (fetchedServices) {
        setServices(fetchedServices);
      } else {
        setError('Failed to fetch services.');
      }
      setLoading(false);
    };

    fetchServices();
  }, [categoryId]);

  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!categoryId) {
    return <div className="text-white text-center">Category ID not found.</div>;
  }

  return (
    <div className="h-screen bg-gray-900 flex flex-col">
      <Header isLoggedIn={true} userName="Alex" onLogout={() => console.log('Logout')} /> {/* Placeholder props */}

      <div className="flex-grow p-6 pt-0 overflow-y-auto">
        <div className="flex items-center mb-6">
          <button onClick={handleBack} className="p-2 rounded-full hover:bg-gray-800 transition mr-2">
            <ArrowLeft className="w-6 h-6 text-white" />
          </button>
          <h2 className="text-2xl font-bold text-white">Services</h2>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search services..."
            className="w-full py-3 pl-10 pr-4 bg-gray-800 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-600"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {loading && <p className="text-white text-center">Loading services...</p>}
        {error && <p className="text-red-500 text-center">{error}</p>}
        {!loading && filteredServices.length === 0 && !error && (
          <p className="text-gray-400 text-center">No services found for this category.</p>
        )}

        <div className="space-y-4">
          {filteredServices.map(service => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>
      </div>

    </div>
  );
};

export default ServiceListPage;
